import { getAdminRealm, type AdminRealm } from '@/lib/adminAuth';

/**
 * CONTENT TEMPLATE LIBRARY — single source of truth.
 *
 * The AI content engine does NOT think in "posts"; it thinks in repeatable
 * content templates. Each template defines a post theme, a hero image, a pool
 * of supporting image angles, and a locked visual style so the feed develops
 * a recognizable premium identity.
 *
 * PEOPLE RATIO POLICY:
 * Templates flagged `people: true` are the ONLY ones whose imagery should
 * feature people as the subject. The generation edge functions use this flag
 * to (a) deal at most ONE people-focused template per generated week and
 * (b) steer shot selection toward scenery, landscapes, luxury hotels and
 * amenities for everything else — keeping the feed dominated by place, not
 * faces.
 *
 * REALMS:
 * • albania / romania  -> TRAVEL_TEMPLATES (Zera Travel)
 * • lewith             -> REAL_ESTATE_TEMPLATES (Lewith & Freeman Real Estate)
 */

export interface ContentTemplate {
  id: number;
  name: string;
  /** Hero image description for this post theme. */
  hero: string;
  /** Pool of supporting image angles to mix and match. */
  supporting: string[];
  /** Locked visual style for the imagery. */
  style: string;
  /** True ONLY when people are the intended subject of the imagery. */
  people?: boolean;
}

export const TRAVEL_TEMPLATES: ContentTemplate[] = [
  {
    id: 1,
    name: 'Destination Spotlight',
    hero: 'golden hour panoramic landscape of the destination, shot with a wide-angle lens',
    supporting: ['aerial drone overview', 'famous landmark', 'charming local streets', 'beach or mountain vista', 'sunset skyline', 'evening skyline with glowing lights', 'quiet local market stalls at dawn'],
    style: 'luxury travel magazine, warm golden tones, natural lighting, no people, pristine empty scenery',
  },
  {
    id: 2,
    name: 'Luxury Hotel Showcase',
    hero: 'hotel front exterior during sunset with warm glowing lighting',
    supporting: ['presidential suite interior', 'infinity pool at dusk', 'balcony view over the sea', 'fine dining table setting', 'serene spa interior', 'private beach with cabanas', 'grand hotel lobby', 'marble bathroom', 'rooftop bar at golden hour', 'elegant breakfast setup'],
    style: 'architectural photography, soft warm lighting, five-star luxury, rich shadows, immaculate unoccupied spaces, no people',
  },
  {
    id: 3,
    name: 'Hidden Gems',
    hero: 'secret beach or hidden waterfall with no crowds',
    supporting: ['mossy forest path', 'dramatic cliff overlook', 'tiny stone village', 'hidden courtyard café', 'crystal-clear river', 'winding mountain road', 'sea cave', 'remote lighthouse'],
    style: 'adventure documentary photography, natural raw beauty, untouched atmosphere, completely empty of people',
  },
  {
    id: 4,
    name: 'Travel Itinerary',
    hero: 'stylized travel map with destination highlights and route markers',
    supporting: ['scenic itinerary stop', 'boutique hotel exterior', 'local restaurant terrace', 'empty coastal road at golden hour', 'panoramic scenic overlook', 'signature landscape moment'],
    style: 'editorial magazine layout, clean composition, sophisticated travel-planning aesthetic, no people',
  },
  {
    id: 5,
    name: 'Customer Testimonial',
    hero: 'real traveler smiling naturally in a beautiful setting',
    supporting: ['joyful airport arrival', 'guided excursion moment', 'traveler at a luxury hotel', 'celebratory dinner', 'couple watching the sunset', 'warm family moment'],
    style: 'authentic lifestyle photography, candid emotion, soft natural light',
    people: true,
  },
  {
    id: 6,
    name: 'Before & After Vacation',
    hero: 'split image: a busy grey office on the left, sunlit Albanian Riviera coastline on the right',
    supporting: ['empty airport departure gate at dawn', 'turquoise beach', 'cocktails on a beach bar table', 'still poolside loungers', 'misty mountain air over peaks'],
    style: 'lifestyle contrast photography, cool muted tones versus warm vivid escape tones, scenery-first, no people',
  },
  {
    id: 7,
    name: 'Drone Photography',
    hero: 'high-altitude aerial view of a dramatic coastline',
    supporting: ['marina full of yachts from above', 'hilltop castle from the air', 'winding mountain pass', 'river canyon', 'beach clubs along the shore', 'historic town rooftops'],
    style: 'cinematic aerial photography, high dynamic range, sweeping scale, crisp detail, no visible people',
  },
  {
    id: 8,
    name: 'Food Spotlight',
    hero: 'beautifully plated traditional Albanian cuisine',
    supporting: ['fresh local ingredients', 'glass of local wine', 'atmospheric restaurant interior', 'outdoor seaside dining table', 'artisan dessert', 'traditional coffee setting'],
    style: 'editorial food photography, natural light, shallow depth of field, rich appetizing color, food and place only, no people',
  },
  {
    id: 9,
    name: 'Cultural Experience',
    hero: 'traditional dancers in folk costume mid-performance',
    supporting: ['vibrant festival scene', 'detailed folk costumes', 'historic church interior', 'elegant mosque architecture', 'lively traditional market', 'local artisan at work'],
    style: 'documentary culture photography, vibrant authentic color, human warmth',
    people: true,
  },
  {
    id: 10,
    name: 'Historical Locations',
    hero: 'ancient castle glowing during sunset',
    supporting: ['worn stone streets', 'medieval towers', 'ancient ruins', 'museum interior', 'centuries-old church', 'close-up architectural details'],
    style: 'heritage photography, warm dramatic light, rich stone texture, timeless mood, empty of people',
  },
  {
    id: 11,
    name: 'Luxury Experiences',
    hero: 'private yacht cruising turquoise water',
    supporting: ['champagne service set out on a yacht deck', 'helicopter over the coast', 'gleaming luxury car on a scenic road', 'sunset dinner table for two', 'luxury SUV on a coastal road', 'secluded private beach'],
    style: 'five-star luxury lifestyle photography, polished, aspirational, golden light, amenities and scenery only, no people',
  },
  {
    id: 12,
    name: 'Seasonal Guide',
    hero: 'representative seasonal landscape at its absolute peak',
    supporting: ['spring wildflowers in bloom', 'sun-drenched vineyards', 'summer beaches', 'boats on turquoise water', 'golden autumn forests', 'wine harvest still life', 'snow-dusted peaks', 'winter mountain village'],
    style: 'seasonal editorial photography, true-to-season color palette, atmospheric light, pure landscape, no people',
  },
  {
    id: 13,
    name: 'Travel Tips',
    hero: 'flat-lay of passport, boarding pass and phone against stunning Albanian scenery backdrop',
    supporting: ['modern airport terminal architecture', 'local currency close-up', 'SIM card and phone setup flat-lay', 'scenic city street', 'open map with route marked', 'ferry crossing blue water'],
    style: 'clean practical lifestyle photography, bright, helpful, uncluttered, objects and places, no people',
  },
  {
    id: 14,
    name: 'Meet the Team',
    hero: 'professional portrait of a travel advisor, confident and warm',
    supporting: ['advisor working at a desk', 'planning trips over maps', 'friendly phone call', 'greeting clients at the airport', 'stylish office space', 'team meeting'],
    style: 'professional corporate lifestyle photography, warm approachable tones',
    people: true,
  },
  {
    id: 15,
    name: 'Client Vacation Highlights',
    hero: 'guest joyfully enjoying the destination',
    supporting: ['candid selfie moment', 'toasting at a restaurant', 'boat tour on clear water', 'arriving at a beautiful hotel', 'memorable excursion'],
    style: 'candid lifestyle photography, genuine joy, sun-kissed natural light',
    people: true,
  },
  {
    id: 16,
    name: 'Myth vs Reality',
    hero: 'split comparison graphic: expectation versus stunning reality',
    supporting: ['pristine empty beaches', 'gleaming luxury hotel', 'safe welcoming streets', 'world-class fine dining table'],
    style: 'clean editorial comparison photography, bright honest light, persuasive clarity, no people',
  },
  {
    id: 17,
    name: 'Day Trips',
    hero: 'scenic open road leading toward the destination',
    supporting: ['charming roadside stop', 'countryside restaurant terrace', 'breathtaking scenic overlook', 'must-see attraction'],
    style: 'road-trip editorial photography, freedom and movement, warm daylight, empty roads, no people',
  },
  {
    id: 18,
    name: 'Balkan Multi-Country Tours',
    hero: 'scenic road crossing dramatic country borders through the Balkans',
    supporting: ['Albanian Riviera coastline', 'Bay of Kotor in Montenegro', 'Prizren old town in Kosovo', 'Lake Ohrid in North Macedonia', 'Greek island harbor'],
    style: 'epic travel editorial, sweeping landscapes, grand cinematic scale, no people',
  },
  {
    id: 19,
    name: 'Adventure Activities',
    hero: 'dramatic alpine ridge trail overlooking jagged mountain peaks',
    supporting: ['empty alpine trail winding upward', 'white-water rapids through a canyon', 'zipline cable stretching over a canyon', 'ATV trail through wild terrain', 'kayak resting on turquoise water', 'crystal-clear sea over a reef'],
    style: 'high-energy adventure photography, dynamic angles, crisp detail, landscape as the hero, no people or at most one tiny distant figure',
  },
  {
    id: 20,
    name: 'Local Legends',
    hero: 'historic castle under dramatic moody skies',
    supporting: ['old painting or fresco', 'weathered statue', 'lamplit historic street', 'ancient church', 'storied monument'],
    style: 'moody cinematic heritage photography, dramatic skies, mythic atmosphere, no people',
  },
  {
    id: 21,
    name: 'Travel Deals',
    hero: 'irresistible luxury destination scene with clean space for a promotional overlay',
    supporting: ['inviting hotel exterior', 'sparkling pool', 'perfect empty beach day', 'signature scenic excursion', 'airplane wing over the coast'],
    style: 'bright promotional travel photography, vivid, aspirational, high-key light, no people',
  },
  {
    id: 22,
    name: 'Behind the Scenes',
    hero: 'travel advisor thoughtfully planning itineraries',
    supporting: ['laptop with itineraries open', 'booking process in action', 'on-site hotel inspection', 'VIP airport pickup', 'team planning meeting'],
    style: 'authentic workplace documentary photography, real and trustworthy',
    people: true,
  },
  {
    id: 23,
    name: 'Packing Guide',
    hero: 'open luxury suitcase, immaculately packed',
    supporting: ['resort wardrobe flat-lay', 'passport and boarding pass', 'travel camera', 'designer sunglasses', 'sunscreen essentials', 'hiking boots'],
    style: 'flat-lay editorial product photography, clean backdrop, perfect styling, no people',
  },
  {
    id: 24,
    name: 'Interactive Post',
    hero: 'beautiful destination composed with space for engagement text overlays',
    supporting: ['two rival beaches side by side', 'two hotels to choose between', 'two iconic dishes', 'two cities face-off', 'mysterious guess-the-location scene'],
    style: 'bold engaging social photography, punchy color, clear focal points, scenery only, no people',
  },
  {
    id: 25,
    name: 'Cinematic Travel Reel',
    hero: 'drone shot of sunrise breaking over the coastline',
    supporting: ['empty old town street at dawn', 'slow-motion waves', 'grand hotel entrance', 'close-up of local food', 'boat gliding across a bay', 'golden hour glow over hills', 'glowing nightlife street scene', 'drone pull-away reveal'],
    style: 'Netflix travel documentary, 24fps cinematic feel, golden hour, high dynamic range, warm cinematic color grading, landscapes and places over people',
  },
];

/**
 * Lewith & Freeman Real Estate template library (realm: lewith).
 * Same engine, real-estate visual language. People appear ONLY in the
 * flagged templates — everything else showcases properties, interiors,
 * amenities and neighborhoods.
 */
export const REAL_ESTATE_TEMPLATES: ContentTemplate[] = [
  {
    id: 1,
    name: 'Featured Listing',
    hero: 'stately home exterior at twilight with warm glowing windows and manicured landscaping',
    supporting: ['grand foyer with staircase', 'sun-filled great room', 'aerial view of the property and grounds', 'covered front porch at golden hour', 'evening curb appeal with landscape lighting'],
    style: 'luxury real estate photography, warm inviting light, magazine quality, immaculate unoccupied spaces, no people',
  },
  {
    id: 2,
    name: 'Just Listed',
    hero: 'pristine home front exterior on a sunny morning, fresh landscaping, wide angle',
    supporting: ['welcoming front door close-up', 'bright open-concept living area', 'backyard with green lawn', 'street view of the tree-lined block'],
    style: 'bright MLS-grade architectural photography, crisp daylight, true-to-color, no people',
  },
  {
    id: 3,
    name: 'Luxury Home Tour',
    hero: 'dramatic two-story great room with floor-to-ceiling windows and designer furnishings',
    supporting: ['chef kitchen with waterfall island', 'primary suite with sitting area', 'spa bathroom with freestanding tub', 'wine cellar', 'home theater', 'indoor pool'],
    style: 'high-end interior design photography, soft natural light, rich textures, editorial styling, no people',
  },
  {
    id: 4,
    name: 'Neighborhood Spotlight',
    hero: 'aerial view of a leafy tree-lined neighborhood with charming homes',
    supporting: ['quaint main street storefronts', 'community park with mature trees', 'lakefront shoreline', 'historic town square', 'local café exterior'],
    style: 'lifestyle editorial photography, warm community feel, golden daylight, quiet empty streets, no people',
  },
  {
    id: 5,
    name: 'Client Success Story',
    hero: 'happy new homeowners holding keys in front of their home',
    supporting: ['family on the front porch', 'couple at the closing table', 'agent handing over keys', 'celebratory moment in the new kitchen'],
    style: 'authentic lifestyle photography, candid joy, soft natural light',
    people: true,
  },
  {
    id: 6,
    name: 'Before & After Staging',
    hero: 'split image: cluttered dated living room on the left, beautifully staged bright living room on the right',
    supporting: ['staged dining room', 'refreshed kitchen styling', 'decluttered primary bedroom', 'styled outdoor patio'],
    style: 'clean comparison interior photography, honest bright light, persuasive clarity, no people',
  },
  {
    id: 7,
    name: 'Aerial Property Views',
    hero: 'high-altitude drone view of an estate with sweeping grounds and mountain backdrop',
    supporting: ['lakefront property from above', 'wooded acreage boundary view', 'rooftop and lot-line overview', 'winding private driveway from the air', 'neighborhood rooftops at sunset'],
    style: 'cinematic aerial photography, high dynamic range, sweeping scale, crisp detail, no visible people',
  },
  {
    id: 8,
    name: 'Kitchen & Bath Showcase',
    hero: 'designer kitchen with quartz island, custom cabinetry and pendant lighting',
    supporting: ['farmhouse sink detail', 'marble bathroom with glass shower', 'butler pantry', 'freestanding soaking tub by a window', 'brass fixture close-ups'],
    style: 'interior detail photography, soft window light, shallow depth of field, immaculate styling, no people',
  },
  {
    id: 9,
    name: 'Community Events',
    hero: 'lively local farmers market with vendors and neighbors',
    supporting: ['summer concert on the green', 'holiday parade on main street', 'charity 5k start line', 'fall festival stalls'],
    style: 'documentary community photography, vibrant authentic color, human warmth',
    people: true,
  },
  {
    id: 10,
    name: 'Historic Homes',
    hero: 'grand Victorian home with original architectural details at golden hour',
    supporting: ['ornate woodwork close-up', 'stained glass window detail', 'wraparound porch', 'original hardwood staircase', 'period fireplace mantel'],
    style: 'heritage architectural photography, warm dramatic light, rich texture, timeless mood, no people',
  },
  {
    id: 11,
    name: 'Luxury Amenities',
    hero: 'resort-style backyard pool with stone patio and outdoor kitchen at dusk',
    supporting: ['home gym with mirrored wall', 'heated three-car garage', 'sauna and spa room', 'tennis court on the grounds', 'dock on a private lake', 'smart-home control panel'],
    style: 'five-star amenity photography, polished, aspirational, golden light, pristine and unoccupied, no people',
  },
  {
    id: 12,
    name: 'Seasonal Market Guide',
    hero: 'beautiful home framed by peak seasonal color — foliage, snow, or spring bloom',
    supporting: ['autumn leaves over a brick colonial', 'snow-dusted farmhouse with glowing windows', 'spring gardens in full bloom', 'summer lakefront evening'],
    style: 'seasonal editorial photography, true-to-season palette, atmospheric light, pure scenery, no people',
  },
  {
    id: 13,
    name: 'Home Buyer Tips',
    hero: 'flat-lay of house keys, pre-approval letter and floor plans on a clean desk',
    supporting: ['calculator and mortgage documents', 'inspection checklist clipboard', 'moving boxes neatly stacked', 'front door with welcome mat', 'contract and pen close-up'],
    style: 'clean practical editorial photography, bright, helpful, uncluttered, objects only, no people',
  },
  {
    id: 14,
    name: 'Meet the Agent',
    hero: 'professional portrait of a real estate agent, confident and approachable',
    supporting: ['agent reviewing listings at a desk', 'walking a client through a home', 'friendly phone consultation', 'team meeting in a modern office'],
    style: 'professional corporate lifestyle photography, warm approachable tones',
    people: true,
  },
  {
    id: 15,
    name: 'Just Sold',
    hero: 'elegant SOLD sign in front of a beautiful home with perfect curb appeal',
    supporting: ['sold rider close-up against blurred home', 'front exterior at golden hour', 'champagne and keys on the kitchen counter', 'welcome mat at the front door'],
    style: 'celebratory real estate photography, warm optimistic light, crisp detail, no people',
  },
  {
    id: 16,
    name: 'Myth vs Reality',
    hero: 'split comparison graphic: real estate expectation versus reality',
    supporting: ['pristine move-in-ready interior', 'transparent pricing documents flat-lay', 'welcoming open house entry', 'sunlit renovated kitchen'],
    style: 'clean editorial comparison photography, bright honest light, persuasive clarity, no people',
  },
  {
    id: 17,
    name: 'Weekend Open House',
    hero: 'inviting open front door with sunlight spilling into a bright entryway',
    supporting: ['directional open house sign on a lawn', 'staged living room ready for visitors', 'fresh flowers on the kitchen island', 'welcome table with brochures'],
    style: 'warm inviting interior photography, soft daylight, immaculate staging, no people',
  },
  {
    id: 18,
    name: 'Commercial Properties',
    hero: 'modern commercial building exterior with glass facade at dusk',
    supporting: ['open-plan office interior', 'retail storefront on main street', 'warehouse interior with high ceilings', 'development land aerial view'],
    style: 'commercial architectural photography, clean lines, professional lighting, unoccupied spaces, no people',
  },
  {
    id: 19,
    name: 'Outdoor Living',
    hero: 'covered patio with stone fireplace, string lights and comfortable seating at dusk',
    supporting: ['fire pit circle overlooking hills', 'landscaped garden path', 'deck with mountain views', 'pergola over an outdoor dining table', 'manicured lawn at golden hour'],
    style: 'outdoor lifestyle photography, golden hour glow, cozy aspirational mood, empty inviting spaces, no people',
  },
  {
    id: 20,
    name: 'Local Landmarks',
    hero: 'iconic regional landmark under dramatic golden skies',
    supporting: ['historic courthouse architecture', 'scenic river gorge overlook', 'covered bridge in the countryside', 'downtown skyline at dusk', 'state park vista'],
    style: 'moody cinematic landscape photography, dramatic skies, regional pride, no people',
  },
  {
    id: 21,
    name: 'New to Market',
    hero: 'irresistible home exterior with clean space for a promotional price overlay',
    supporting: ['sparkling pool and patio', 'bright staged interior', 'sweeping front lawn', 'sunset view from the back deck'],
    style: 'bright promotional real estate photography, vivid, aspirational, high-key light, no people',
  },
  {
    id: 22,
    name: 'Behind the Scenes',
    hero: 'agent thoughtfully preparing a listing presentation',
    supporting: ['photographer shooting a listing', 'staging team styling a room', 'lockbox and keys close-up', 'marketing plan on a laptop screen'],
    style: 'authentic workplace documentary photography, real and trustworthy',
    people: true,
  },
  {
    id: 23,
    name: 'Moving Day Guide',
    hero: 'neatly packed moving boxes and labeled essentials in a bright empty room',
    supporting: ['moving checklist flat-lay', 'keys and contract on a counter', 'measuring tape and floor plan', 'welcome basket at the front door'],
    style: 'flat-lay editorial product photography, clean backdrop, perfect styling, no people',
  },
  {
    id: 24,
    name: 'Interactive Post',
    hero: 'beautiful home composed with space for engagement text overlays',
    supporting: ['two kitchens to choose between', 'modern farmhouse versus classic colonial', 'city condo versus country estate', 'guess-the-listing-price home exterior'],
    style: 'bold engaging social photography, punchy color, clear focal points, properties only, no people',
  },
  {
    id: 25,
    name: 'Cinematic Listing Reel',
    hero: 'drone shot of sunrise breaking over a hillside estate',
    supporting: ['slow glide through the front door', 'light sweeping across a chef kitchen', 'pool water rippling at dusk', 'golden hour over the backyard', 'drone pull-away reveal of the property'],
    style: 'cinematic real estate film, 24fps feel, golden hour, high dynamic range, warm color grading, architecture over people',
  },
];
/**
 * The MTN Shop template library (realm: mtnshop — /SocialPopAdmin hub).
 * Rigging & live-event production gear. People appear ONLY in the flagged
 * templates — everything else showcases gear, rigs, stages and venues.
 */
export const MTN_SHOP_TEMPLATES: ContentTemplate[] = [
  {
    id: 1,
    name: 'Product Spotlight',
    hero: 'professional chain hoist on a clean studio backdrop, dramatic side lighting, crisp product detail',
    supporting: ['close-up of the load chain and hook', 'control pendant detail', 'gear staged in a road case', 'product hanging from a truss stick', 'rated capacity tag macro shot'],
    style: 'premium industrial product photography, dark backdrop, dramatic rim lighting, crisp mechanical detail, no people',
  },
  {
    id: 2,
    name: 'Rigging Safety',
    hero: 'perfectly dressed rigging point with shackle, steel and span set on an arena beam',
    supporting: ['inspection tag close-up', 'properly seized shackle pin', 'load cell reading on a hoist', 'organized rigging gear laid out for inspection', 'fall-protection harness flat-lay'],
    style: 'documentary industrial photography, honest available light, sharp technical detail, safety-first mood, no people',
  },
  {
    id: 3,
    name: 'Stage Build Timelapse',
    hero: 'festival main stage mid-build with truss towers rising at dawn',
    supporting: ['empty field with staked ground plan', 'truss grid at half height', 'roof canopy going up', 'finished stage under show lighting at dusk'],
    style: 'cinematic wide-angle production photography, golden hour, epic scale, silhouetted structures, no identifiable people',
  },
  {
    id: 4,
    name: 'Gear in Action',
    hero: 'chain hoists flying a lighting rig above an arena floor during load-in',
    supporting: ['truss grid loaded with moving lights', 'tower lift raising a line array', 'motors climbing chain in unison', 'followspot chair rigged high in the steel'],
    style: 'gritty live-production photography, atmospheric haze, beam light accents, high contrast, machines as the hero, no people',
  },
  {
    id: 5,
    name: 'Crew Story',
    hero: 'rigger in harness and hard hat working high steel above an arena, confident and focused',
    supporting: ['crew pushing road cases up a ramp', 'down-rigger flagging a point', 'team huddle before load-in', 'hands threading a shackle'],
    style: 'authentic documentary crew photography, real work, natural light, respectful trade pride',
    people: true,
  },
  {
    id: 6,
    name: 'Tool Tuesday',
    hero: 'flat-lay of essential rigging tools — wrenches, gloves, tape, torch — on worn road case lid',
    supporting: ['podger wrench close-up', 'gaff tape stack', 'multi-tool detail', 'chalk-marked steel and tools', 'tool belt fully loaded'],
    style: 'flat-lay editorial product photography, textured industrial backdrop, moody warm light, objects only, no people',
  },
  {
    id: 7,
    name: 'Before & After Rig',
    hero: 'split image: empty arena floor on the left, fully rigged show with flown PA and lighting on the right',
    supporting: ['bare venue ceiling steel', 'marked-out floor plan with chalk points', 'finished rig glowing under show light', 'audience-ready stage'],
    style: 'clean comparison photography, wide symmetrical composition, dramatic transformation, no people',
  },
  {
    id: 8,
    name: 'Warehouse Tour',
    hero: 'organized production warehouse aisle with racked truss, motors and cases under industrial lighting',
    supporting: ['wall of chain hoists on test', 'truss stacked to the ceiling', 'cable trunks labeled and staged', 'shipping dock with palletized gear'],
    style: 'clean industrial interior photography, deep perspective lines, cool tones with warm accents, immaculate order, no people',
  },
  {
    id: 9,
    name: 'Spec Breakdown',
    hero: 'exploded-view style product shot of a chain hoist with clean space for spec callouts',
    supporting: ['WLL tag macro', 'chain gauge detail', 'hook throat close-up', 'motor housing badge', 'dimensions-ready side profile on white'],
    style: 'technical catalog photography, bright even light, white or steel backdrop, engineering precision, no people',
  },
  {
    id: 10,
    name: 'New Arrivals',
    hero: 'fresh pallet of boxed gear being unbanded in the warehouse, product front and center',
    supporting: ['unboxed hoist with packing removed', 'row of brand-new shackles', 'new truss sticks with stickers on', 'stacked cases with fresh labels'],
    style: 'bright promotional product photography, crisp daylight, vivid and aspirational, gear only, no people',
  },
  {
    id: 11,
    name: 'Customer Problem Solver',
    hero: 'tricky rigging scenario — an awkward venue beam with a clean, correctly rigged solution in place',
    supporting: ['beam clamp on odd steel', 'spanset wrapped basket around a girder', 'low-headroom hoist configuration', 'bridle solving an offset point'],
    style: 'real-world technical photography, honest venue light, problem-and-solution clarity, no people',
  },
  {
    id: 12,
    name: 'Maintenance Monday',
    hero: 'chain hoist opened on a clean bench for inspection, parts laid out methodically',
    supporting: ['chain being cleaned and oiled', 'brake assembly close-up', 'test weights on the floor scale', 'inspection checklist on a clipboard'],
    style: 'workshop documentary photography, warm bench light, methodical care, tools and parts only, no people',
  },
  {
    id: 13,
    name: 'Behind the Scenes',
    hero: 'production office trailer with rigging plots and radios, show prep in motion',
    supporting: ['rigging plot spread on a case lid', 'labeled motor distro', 'chalk marks and point numbers on the floor', 'radio charging bank'],
    style: 'authentic backstage documentary photography, real and trustworthy, warm practical light',
    people: true,
  },
  {
    id: 14,
    name: 'Gear Deals',
    hero: 'hero product on a dark stage floor with clean negative space for a promotional overlay',
    supporting: ['bundle of shackles and steel', 'hoist and controller kit', 'truss package staged for shipping', 'PPE bundle flat-lay'],
    style: 'bold promotional product photography, dramatic spot lighting, punchy contrast, clear focal point, no people',
  },
  {
    id: 15,
    name: 'Interactive Post',
    hero: 'two rigging setups side by side composed with space for engagement text overlays',
    supporting: ['two hoists face-off', 'guess-the-load scenario', 'spot-the-error rigging point', 'this-or-that tool comparison'],
    style: 'bold engaging social photography, punchy color, clear focal points, gear only, no people',
  },
  {
    id: 16,
    name: 'Cinematic Load-In Reel',
    hero: 'drone shot of trucks lined up at an arena dock at sunrise',
    supporting: ['cases rolling up the ramp in slow motion', 'motors climbing chain in sync', 'grid coming alive with light', 'pull-away reveal of the finished rig'],
    style: 'cinematic production documentary, 24fps feel, atmospheric haze, high dynamic range, machines and scale over people',
  },
];

/** Library for a specific admin realm. */
export function templatesForRealm(realm: AdminRealm): ContentTemplate[] {
  if (realm === 'lewith') return REAL_ESTATE_TEMPLATES;
  if (realm === 'mtnshop') return MTN_SHOP_TEMPLATES;
  return TRAVEL_TEMPLATES;
}

/** Backward-compatible export — the ACTIVE realm's library. */
export const CONTENT_TEMPLATES: ContentTemplate[] = TRAVEL_TEMPLATES;

/** Compact wire format sent to the generation edge functions (active realm). */
export function templatesForApi() {
  return templatesForRealm(getAdminRealm()).map((t) => ({
    name: t.name,
    hero: t.hero,
    supporting: t.supporting,
    style: t.style,
    people: !!t.people,
  }));
}

/**
 * Finds the template a post belongs to by matching its theme prefix.
 * Realm-aware: checks the given (or active) realm's library first, then the
 * other libraries so legacy posts still resolve.
 */
export function templateForTheme(
  theme: string | null | undefined,
  realm?: AdminRealm,
): ContentTemplate | null {
  if (!theme) return null;
  const lower = theme.toLowerCase();
  const primary = templatesForRealm(realm || getAdminRealm());
  const all = [primary, TRAVEL_TEMPLATES, REAL_ESTATE_TEMPLATES, MTN_SHOP_TEMPLATES];
  for (const lib of all) {
    const hit = lib.find((t) => lower.startsWith(t.name.toLowerCase()));
    if (hit) return hit;
  }
  return null;
}

